/**
 * Mock data layer for the Energy &amp; Productivity panel. Wraps a fake async
 * call so the calling hook mirrors the real API integration that will replace
 * it once the back-end endpoints exist.
 */
const COMMON_TICK_LABELS = ["381", "433", "464", "381", "381", "381"];
/** Base bar heights per metric, before asset-filter scaling. */
const BAR_HEIGHTS_MATERIAL_MOVED = [1600, 1300, 975, 1100, 1850, 1425];
const BAR_HEIGHTS_ENERGY_USED = [1450, 1175, 1825, 925, 1500, 1675];
const BAR_HEIGHTS_LOAD_COUNT = [29, 37, 43, 12, 57, 49];
const BAR_HEIGHTS_IDLE_TIME = [49, 61, 72, 21, 52, 81];
const HAULERS_FILTER_MULTIPLIER = 0.65;
const LOADERS_FILTER_MULTIPLIER = 0.45;
const CYCLE_TIME_LOAD_PERCENT = 25;
const CYCLE_TIME_CHARGING_PERCENT = 35;
const CYCLE_TIME_IDLE_PERCENT = 25;
const CYCLE_TIME_CARRY_PERCENT = 15;
const COLOR_CYCLE_LOAD = "#777777";
const COLOR_CYCLE_CHARGING = "#0DB3A0";
const COLOR_CYCLE_IDLE = "#86BAFF";
const COLOR_CYCLE_CARRY = "#FFA230";
/** Base bar heights per metric, before asset-filter scaling. */
const METRIC_BAR_HEIGHTS = {
    EnergyUsed: BAR_HEIGHTS_ENERGY_USED,
    IdleTime: BAR_HEIGHTS_IDLE_TIME,
    LoadCount: BAR_HEIGHTS_LOAD_COUNT,
    MaterialMoved: BAR_HEIGHTS_MATERIAL_MOVED
};
/** Display unit shown in legends / in-bar text. */
const METRIC_UNITS = {
    EnergyUsed: "kWh",
    IdleTime: "%",
    LoadCount: "cycles",
    MaterialMoved: "tonnes"
};
/** Asset-filter multiplier — Loaders/Haulers move less than the full fleet. */
const ASSET_FILTER_MULTIPLIER = {
    All: 1,
    Haulers: HAULERS_FILTER_MULTIPLIER,
    Loaders: LOADERS_FILTER_MULTIPLIER
};
/** Slices powering the Cycle Time donut chart. */
const CYCLE_TIME_SLICES = [
    { color: COLOR_CYCLE_LOAD, label: "Load", value: CYCLE_TIME_LOAD_PERCENT },
    { color: COLOR_CYCLE_CHARGING, label: "Charging", value: CYCLE_TIME_CHARGING_PERCENT },
    { color: COLOR_CYCLE_IDLE, label: "Idle", value: CYCLE_TIME_IDLE_PERCENT },
    { color: COLOR_CYCLE_CARRY, label: "Carry", value: CYCLE_TIME_CARRY_PERCENT }
];
/** Activity-table rows rendered beneath the Cycle Time donut. */
const CYCLE_TIME_ACTIVITIES = [
    { label: "Load", timeLabel: "1 hr" },
    { label: "Carry", timeLabel: "1 hr" },
    { label: "Dump", timeLabel: "1 hr" },
    { label: "Idle", timeLabel: "1 hr" }
];
const SUPPORTED_METRICS = new Set([
    "MaterialMoved",
    "EnergyUsed",
    "LoadCount",
    "IdleTime",
    "CycleTime"
]);
export const isSupportedEnergyProductivityMetric = (metric) => SUPPORTED_METRICS.has(metric);
const buildBarSeries = (metric, assetFilter, dateKey) => {
    const baseHeights = METRIC_BAR_HEIGHTS[metric] ?? [];
    const multiplier = ASSET_FILTER_MULTIPLIER[assetFilter];
    const scaled = baseHeights.map((value) => Math.round(value * multiplier));
    return {
        assetFilter,
        data: scaled,
        dateKey,
        metric,
        tickLabels: COMMON_TICK_LABELS,
        type: "bar",
        unit: METRIC_UNITS[metric] ?? ""
    };
};
const buildDonutSeries = (assetFilter, dateKey) => ({
    activities: CYCLE_TIME_ACTIVITIES,
    assetFilter,
    dateKey,
    metric: "CycleTime",
    slices: CYCLE_TIME_SLICES,
    type: "donut"
});
/**
 * Returns a chart series for the given metric / asset filter / date. The
 * returned promise simulates network latency so consumers can exercise their
 * loading state in tests and storybook.
 */
export const fetchEnergyProductivityMockSeries = (metric, assetFilter, dateKey) => {
    if (!isSupportedEnergyProductivityMetric(metric)) {
        return Promise.reject(new Error(`Unsupported metric: ${metric}`));
    }
    const series = metric === "CycleTime" ? buildDonutSeries(assetFilter, dateKey) : buildBarSeries(metric, assetFilter, dateKey);
    return Promise.resolve(series);
};
